import BarChart from "components/charts/BarChart";
import { ChartSkeleton } from "components/charts/ChartSkeleton";
import LineChart, { LineChartSeries } from "components/charts/LineChart";
import {
  AskStrikeUpButton,
  Badge,
  Box,
  Card,
  DateRangePicker,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  KpiTile,
  PreHeader,
  RefreshButton,
  Spinner,
  Stack,
  Table,
  Text,
  Tooltip,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useMemo, useState } from "react";
import {
  MdCheckCircle,
  MdError,
  MdPeople,
  MdSecurity,
  MdWarning,
} from "react-icons/md";
import { useLocation } from "react-router-dom";
import {
  CustomerHealthResponse,
  AutonomousAgentUsageStats,
  CustomerHealthStatus,
  dashboardService,
  EndpointStats,
  EngagementFunnelResponse,
  FeedbackDailyTrendPoint,
  FeedbackNegativeReasonPoint,
  SecurityViolationStatsResponse,
  SessionQualityResponse,
  SkillUsageStats,
} from "services/dashboardService";
import AllSkillsTableCard from "./components/AllSkillsTableCard";
import { getMockDashboardData } from "./dashboardMockData";
import { useDashboardRange } from "./useDashboardRange";

/** Which of the three sub-pages this shell renders. */
export type DashboardSection = "overview" | "usage" | "performance";

// Which datasets each sub-page needs to fetch. Kept minimal so every page only loads its own
// charts (e.g. Overview pulls the four hero-KPI sources + messages + funnel; nothing else).
const SECTION_NEEDS: Record<DashboardSection, string[]> = {
  overview: ["messages", "funnel", "quality", "feedbackTrend", "health"],
  usage: ["messages", "dailyUsers", "topEndpoints", "topSkills", "autonomous"],
  performance: [
    "toolExecutions",
    "quality",
    "security",
    "feedbackTrend",
    "negativeReasons",
    "health",
  ],
};

// Card-level chart wrapper — pre-header label (mono UPPERCASE) above heading,
// optional spinner during refresh.
const ChartCardHeader = ({
  preLabel,
  title,
  refreshing,
}: {
  preLabel: string;
  title: string;
  refreshing?: boolean;
}) => (
  <Card.Header pb={2}>
    <Flex justify="space-between" align="flex-start" gap={3}>
      <Box>
        <PreHeader size="xs">{preLabel}</PreHeader>
        <Heading size="md" mt={2}>
          {title}
        </Heading>
      </Box>
      {refreshing && (
        <Spinner size="sm" color="var(--chakra-colors-blue-solid)" />
      )}
    </Flex>
  </Card.Header>
);

interface DashboardProps {
  /** Sub-page to render. Defaults to overview when used standalone. */
  section?: DashboardSection;
}

const Dashboard = ({ section = "overview" }: DashboardProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  const need = (key: string) => SECTION_NEEDS[section].includes(key);
  const isOverview = section === "overview";
  const isUsage = section === "usage";
  const isPerformance = section === "performance";

  // Hidden demo mode persistence (unchanged behaviour).
  const demoParam = new URLSearchParams(location.search).get("demo");
  useEffect(() => {
    if (demoParam === "true") localStorage.setItem("demoMode", "true");
    else if (demoParam === "false") localStorage.removeItem("demoMode");
  }, [demoParam]);
  const isDemoMode =
    demoParam === "true" ||
    (demoParam === null && localStorage.getItem("demoMode") === "true");

  // Shared date range (persisted across sub-pages).
  const { range, setPreset, setCustom, startDate, endDate, spanHours } =
    useDashboardRange();
  const rangeParams = { startDate, endDate };
  const rangeDays = Math.max(1, Math.round(spanHours / 24));

  const rangeLabel = useMemo(() => {
    if (range.preset === "7") return t("app.dashboard.timeRange.last7Days");
    if (range.preset === "14") return t("app.dashboard.timeRange.last14Days");
    if (range.preset === "30") return t("app.dashboard.timeRange.last30Days");
    return `${range.from} – ${range.to}`;
  }, [range, t]);

  const [messagesData, setMessagesData] = useState<any[]>([]);
  const [toolExecutionsSeries, setToolExecutionsSeries] = useState<
    LineChartSeries[]
  >([]);
  const [dailyActiveUsers, setDailyActiveUsers] = useState<LineChartSeries[]>(
    [],
  );
  const [topEndpoints, setTopEndpoints] = useState<EndpointStats[]>([]);
  const [topSkills, setTopSkills] = useState<SkillUsageStats[]>([]);
  const [autonomousUsage, setAutonomousUsage] = useState<
    AutonomousAgentUsageStats[]
  >([]);
  const [feedbackTrend, setFeedbackTrend] = useState<any[]>([]);
  const [negativeFeedbackReasons, setNegativeFeedbackReasons] = useState<any[]>(
    [],
  );
  const [securityViolations, setSecurityViolations] =
    useState<SecurityViolationStatsResponse | null>(null);
  const [engagementFunnel, setEngagementFunnel] =
    useState<EngagementFunnelResponse | null>(null);
  const [sessionQuality, setSessionQuality] =
    useState<SessionQualityResponse | null>(null);
  const [customerHealth, setCustomerHealth] =
    useState<CustomerHealthResponse | null>(null);

  // Independent loading + loaded flags per chart.
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [toolExecutionsLoading, setToolExecutionsLoading] = useState(true);
  const [dailyUsersLoading, setDailyUsersLoading] = useState(true);
  const [topEndpointsLoading, setTopEndpointsLoading] = useState(true);
  const [topSkillsLoading, setTopSkillsLoading] = useState(true);
  const [autonomousUsageLoading, setAutonomousUsageLoading] = useState(true);
  const [feedbackTrendLoading, setFeedbackTrendLoading] = useState(true);
  const [feedbackReasonsLoading, setFeedbackReasonsLoading] = useState(true);
  const [securityLoading, setSecurityLoading] = useState(true);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [qualityLoading, setQualityLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);

  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [toolExecutionsLoaded, setToolExecutionsLoaded] = useState(false);
  const [dailyUsersLoaded, setDailyUsersLoaded] = useState(false);
  const [topEndpointsLoaded, setTopEndpointsLoaded] = useState(false);
  const [topSkillsLoaded, setTopSkillsLoaded] = useState(false);
  const [autonomousUsageLoaded, setAutonomousUsageLoaded] = useState(false);
  const [feedbackTrendLoaded, setFeedbackTrendLoaded] = useState(false);
  const [feedbackReasonsLoaded, setFeedbackReasonsLoaded] = useState(false);
  const [securityLoaded, setSecurityLoaded] = useState(false);
  const [funnelLoaded, setFunnelLoaded] = useState(false);
  const [qualityLoaded, setQualityLoaded] = useState(false);
  const [healthLoaded, setHealthLoaded] = useState(false);

  const [isRefreshing, setIsRefreshing] = useState(false);
  // Bumped on refresh; forwarded to the self-fetching AllSkills card.
  const [reloadKey, setReloadKey] = useState(0);

  const loadMockData = (days: number) => {
    const mock = getMockDashboardData(days);
    setMessagesData(mock.messagesData);
    setToolExecutionsSeries(mock.toolExecutionsSeries);
    setDailyActiveUsers(mock.dailyActiveUsers);
    setTopEndpoints(mock.topEndpoints);
    setTopSkills(mock.topSkills);
    setFeedbackTrend(mock.feedbackTrend);
    setNegativeFeedbackReasons(mock.negativeFeedbackReasons);
    setSecurityViolations(mock.securityViolations);
    setEngagementFunnel(mock.engagementFunnel);
    setSessionQuality(mock.sessionQuality);
    setCustomerHealth(mock.customerHealth);
    setAutonomousUsage([]);
    [
      setMessagesLoading,
      setToolExecutionsLoading,
      setDailyUsersLoading,
      setTopEndpointsLoading,
      setTopSkillsLoading,
      setFeedbackTrendLoading,
      setFeedbackReasonsLoading,
      setSecurityLoading,
      setFunnelLoading,
      setQualityLoading,
      setHealthLoading,
      setAutonomousUsageLoading,
    ].forEach((s) => s(false));
    [
      setMessagesLoaded,
      setToolExecutionsLoaded,
      setDailyUsersLoaded,
      setTopEndpointsLoaded,
      setTopSkillsLoaded,
      setFeedbackTrendLoaded,
      setFeedbackReasonsLoaded,
      setSecurityLoaded,
      setFunnelLoaded,
      setQualityLoaded,
      setHealthLoaded,
      setAutonomousUsageLoaded,
    ].forEach((s) => s(true));
  };

  const loadChartData = async () => {
    if (isDemoMode) {
      loadMockData(rangeDays);
      return;
    }

    const hours = Math.max(1, Math.round(spanHours));
    const isHourlyView = spanHours <= 48;

    const formatBucket = (timestamp: string) => {
      const date = new Date(timestamp);
      return isHourlyView
        ? date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
        : date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
    };

    if (need("messages")) {
      setMessagesLoading(true);
      dashboardService
        .getMessagesTimeSeries(hours, rangeParams)
        .then((messagesResponse) => {
          const timestamps =
            messagesResponse.series[0]?.dataPoints.map((dp) => dp.timestamp) ||
            [];
          const chartData = timestamps.map((timestamp, index) => {
            const point: any = { time: formatBucket(timestamp) };
            messagesResponse.series.forEach((series) => {
              point[series.label] = series.dataPoints[index]?.count || 0;
            });
            return point;
          });
          setMessagesData(chartData);
          setMessagesLoaded(true);
        })
        .catch((error) =>
          console.error(
            t("app.dashboard.errors.failedToLoadMessagesChart"),
            error,
          ),
        )
        .finally(() => setMessagesLoading(false));
    }

    if (need("toolExecutions")) {
      setToolExecutionsLoading(true);
      dashboardService
        .getToolExecutionsTimeSeries(hours, rangeParams)
        .then((toolExecutionsResponse) => {
          const series = toolExecutionsResponse.series.map((s) => ({
            id: s.label,
            label: s.label,
            data: s.dataPoints.map((dp) => ({
              x: formatBucket(dp.timestamp),
              y: dp.count,
            })),
          }));
          setToolExecutionsSeries(series);
          setToolExecutionsLoaded(true);
        })
        .catch((error) =>
          console.error(
            t("app.dashboard.errors.failedToLoadToolExecutionsChart"),
            error,
          ),
        )
        .finally(() => setToolExecutionsLoading(false));
    }

    if (need("dailyUsers")) {
      setDailyUsersLoading(true);
      dashboardService
        .getDailyActiveUsers(rangeDays, rangeParams)
        .then((dailyUsersResponse) => {
          if (dailyUsersResponse.series.length > 0) {
            setDailyActiveUsers([
              {
                id: "users",
                label: t("app.dashboard.charts.activeUsers"),
                data: dailyUsersResponse.series[0].dataPoints.map((dp) => ({
                  x: new Date(dp.timestamp).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                  }),
                  y: dp.count,
                })),
              },
            ]);
          } else {
            setDailyActiveUsers([]);
          }
          setDailyUsersLoaded(true);
        })
        .catch((error) =>
          console.error(
            t("app.dashboard.errors.failedToLoadDailyUsersChart"),
            error,
          ),
        )
        .finally(() => setDailyUsersLoading(false));
    }

    if (need("topEndpoints")) {
      setTopEndpointsLoading(true);
      dashboardService
        .getTopEndpoints(rangeDays, 10, rangeParams)
        .then((res) => setTopEndpoints(res?.endpoints ?? []))
        .catch((error) =>
          console.error(
            t("app.dashboard.errors.failedToLoadTopEndpointsChart"),
            error,
          ),
        )
        .finally(() => {
          setTopEndpointsLoaded(true);
          setTopEndpointsLoading(false);
        });
    }

    if (need("topSkills")) {
      setTopSkillsLoading(true);
      dashboardService
        .getTopSkills(rangeDays, 10, rangeParams)
        .then((res) => setTopSkills(res?.skills ?? []))
        .catch((error) =>
          console.error(
            t("app.dashboard.errors.failedToLoadTopSkillsChart"),
            error,
          ),
        )
        .finally(() => {
          setTopSkillsLoaded(true);
          setTopSkillsLoading(false);
        });
    }

    if (need("autonomous")) {
      setAutonomousUsageLoading(true);
      dashboardService
        .getAutonomousAgentUsage(rangeDays, 10)
        .then((autonomousUsageResponse) => {
          if (autonomousUsageResponse && autonomousUsageResponse.agents) {
            setAutonomousUsage(autonomousUsageResponse.agents);
          } else {
            setAutonomousUsage([]);
          }
          setAutonomousUsageLoaded(true);
        })
        .catch((error) =>
          console.error(
            t("app.dashboard.errors.failedToLoadAutonomousUsageChart"),
            error,
          ),
        )
        .finally(() => setAutonomousUsageLoading(false));
    }

    if (need("feedbackTrend")) {
      setFeedbackTrendLoading(true);
      dashboardService
        .getFeedbackDailyTrend(rangeDays, rangeParams)
        .then((res: FeedbackDailyTrendPoint[]) => {
          setFeedbackTrend(
            res.map((point) => ({
              date: new Date(point.date).toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
              }),
              Positive: point.positiveCount,
              Negative: point.negativeCount,
            })),
          );
          setFeedbackTrendLoaded(true);
        })
        .catch((error) =>
          console.error("Failed to load feedback trend chart", error),
        )
        .finally(() => setFeedbackTrendLoading(false));
    }

    if (need("negativeReasons")) {
      setFeedbackReasonsLoading(true);
      dashboardService
        .getNegativeFeedbackByReason(rangeDays, rangeParams)
        .then((res: FeedbackNegativeReasonPoint[]) => {
          setNegativeFeedbackReasons(
            res.map((point) => ({
              reason: point.reasonTag,
              count: point.count,
            })),
          );
          setFeedbackReasonsLoaded(true);
        })
        .catch((error) =>
          console.error("Failed to load negative feedback reason chart", error),
        )
        .finally(() => setFeedbackReasonsLoading(false));
    }
  };

  const loadNewMetrics = async () => {
    if (isDemoMode) {
      loadMockData(rangeDays);
      return;
    }

    if (need("security")) {
      setSecurityLoading(true);
      dashboardService
        .getSecurityViolations(rangeDays, rangeParams)
        .then((data) => {
          setSecurityViolations(data);
          setSecurityLoaded(true);
        })
        .catch((error) =>
          console.error("Failed to load security violations", error),
        )
        .finally(() => setSecurityLoading(false));
    }

    if (need("funnel")) {
      setFunnelLoading(true);
      dashboardService
        .getEngagementFunnel(rangeDays, rangeParams)
        .then((data) => {
          setEngagementFunnel(data);
          setFunnelLoaded(true);
        })
        .catch((error) =>
          console.error("Failed to load engagement funnel", error),
        )
        .finally(() => setFunnelLoading(false));
    }

    if (need("quality")) {
      setQualityLoading(true);
      dashboardService
        .getSessionQuality(rangeDays, rangeParams)
        .then((data) => {
          setSessionQuality(data);
          setQualityLoaded(true);
        })
        .catch((error) =>
          console.error("Failed to load session quality", error),
        )
        .finally(() => setQualityLoading(false));
    }

    if (need("health")) {
      setHealthLoading(true);
      dashboardService
        .getCustomerHealth(rangeDays, rangeParams)
        .then((data) => {
          setCustomerHealth(data);
          setHealthLoaded(true);
        })
        .catch((error) =>
          console.error("Failed to load customer health", error),
        )
        .finally(() => setHealthLoading(false));
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setReloadKey((k) => k + 1);
    await Promise.all([loadChartData(), loadNewMetrics()]);
    setIsRefreshing(false);
  };

  useEffect(() => {
    loadChartData();
    loadNewMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, section]);

  // Hero KPI values (Overview only).
  const heroKpis = useMemo(() => {
    const sessionsStage =
      engagementFunnel?.stages?.find((s) =>
        s.stageName?.toLowerCase().includes("session"),
      ) ?? engagementFunnel?.stages?.[1];
    const sessionsCount = sessionsStage?.count ?? 0;
    const resolutionRate = sessionQuality?.quality?.resolutionRate ?? 0;
    const totalFeedback = feedbackTrend.reduce(
      (acc, p) => acc + (p.Positive ?? 0) + (p.Negative ?? 0),
      0,
    );
    const totalNegative = feedbackTrend.reduce(
      (acc, p) => acc + (p.Negative ?? 0),
      0,
    );
    const negativeShare =
      totalFeedback > 0 ? (totalNegative / totalFeedback) * 100 : 0;
    const atRiskCount =
      (customerHealth?.atRiskCount ?? 0) + (customerHealth?.churningCount ?? 0);
    return { sessionsCount, resolutionRate, negativeShare, atRiskCount };
  }, [engagementFunnel, sessionQuality, feedbackTrend, customerHealth]);

  const hasAnyRisk = heroKpis.atRiskCount > 0;

  const sectionTitle = isOverview
    ? t("app.dashboard.sectionTitles.overview")
    : isUsage
      ? t("app.dashboard.sectionTitles.usage")
      : t("app.dashboard.sectionTitles.performance");

  return (
    <Stack gap={6} w="full" maxW="full">
      {/* Page header — shared date-range picker applies to every sub-page. */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
        <Heading size="xl">{sectionTitle}</Heading>
        <HStack gap={3} wrap="nowrap" flexShrink={0}>
          <AskStrikeUpButton context={t("app.askStrikeUp.context.dashboard")} />
          <DateRangePicker
            preset={range.preset}
            from={range.from}
            to={range.to}
            onPreset={setPreset}
            onCustom={setCustom}
            labels={{
              last7: t("app.dashboard.timeRange.last7Days"),
              last14: t("app.dashboard.timeRange.last14Days"),
              last30: t("app.dashboard.timeRange.last30Days"),
              custom: t("app.dashboard.timeRange.custom"),
              from: t("app.dashboard.timeRange.from"),
              to: t("app.dashboard.timeRange.to"),
            }}
          />
          <RefreshButton onClick={handleRefresh} loading={isRefreshing} />
        </HStack>
      </Flex>

      {/* ── OVERVIEW ─────────────────────────────────────────────────────── */}
      {isOverview && (
        <>
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={5}
          >
            <KpiTile
              label={t("app.dashboard.kpis.activeSessions")}
              value={funnelLoaded ? heroKpis.sessionsCount : "—"}
              sublabel={rangeLabel}
            />
            <KpiTile
              label={t("app.dashboard.kpis.resolutionRate")}
              value={
                qualityLoaded ? `${heroKpis.resolutionRate.toFixed(0)}%` : "—"
              }
              sublabel={t("app.dashboard.kpis.resolutionRateSub")}
              delta={
                qualityLoaded
                  ? `${heroKpis.resolutionRate >= 50 ? "↑" : "↓"}`
                  : undefined
              }
              trend={heroKpis.resolutionRate >= 50 ? "up" : "down"}
            />
            <KpiTile
              label={t("app.dashboard.kpis.negativeShare")}
              value={
                feedbackTrendLoaded
                  ? `${heroKpis.negativeShare.toFixed(0)}%`
                  : "—"
              }
              sublabel={t("app.dashboard.kpis.negativeShareSub")}
              trend={heroKpis.negativeShare > 30 ? "down" : "flat"}
            />
            <KpiTile
              label={t("app.dashboard.kpis.atRiskCustomers")}
              value={healthLoaded ? heroKpis.atRiskCount : "—"}
              sublabel={t("app.dashboard.kpis.atRiskSub")}
              trend={hasAnyRisk ? "down" : "flat"}
            />
          </Grid>

          <Grid
            templateColumns={{ base: "1fr", lg: "repeat(12, 1fr)" }}
            gap={5}
            w="full"
          >
            <GridItem colSpan={{ base: 1, lg: 8 }}>
              <Card.Root h="full">
                <ChartCardHeader
                  preLabel={`${t("app.dashboard.preHeaderActivity")} · ${rangeLabel}`}
                  title={t("app.dashboard.charts.messages")}
                  refreshing={messagesLoading && messagesLoaded}
                />
                <Card.Body>
                  {!messagesLoaded ? (
                    <ChartSkeleton height={280} withLegend />
                  ) : (
                    <BarChart
                      data={messagesData}
                      keys={
                        messagesData.length > 0
                          ? Object.keys(messagesData[0]).filter(
                              (k) => k !== "time",
                            )
                          : []
                      }
                      indexBy="time"
                      height={280}
                    />
                  )}
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem colSpan={{ base: 1, lg: 4 }}>
              <Card.Root h="full">
                <ChartCardHeader
                  preLabel={t("app.dashboard.preHeaderFunnel")}
                  title={t("app.dashboard.charts.engagementFunnel")}
                />
                <Card.Body>
                  {!funnelLoaded ? (
                    <ChartSkeleton height={280} />
                  ) : engagementFunnel && engagementFunnel.stages.length > 0 ? (
                    <VStack gap={2} align="stretch">
                      {engagementFunnel.stages.map((stage, idx) => (
                        <Box
                          key={idx}
                          pb={2}
                          borderBottom={
                            idx < engagementFunnel.stages.length - 1
                              ? "1px solid"
                              : "none"
                          }
                          borderColor="var(--border-subtle)"
                        >
                          <PreHeader size="xs">{stage.stageName}</PreHeader>
                          <Flex align="baseline" justify="space-between" mt={1}>
                            <Heading
                              size="md"
                              letterSpacing="-0.03em"
                              style={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {stage.count}
                            </Heading>
                            <Text
                              fontFamily="var(--font-mono)"
                              fontSize="11px"
                              letterSpacing="0.06em"
                              color={
                                stage.overallConversionRate > 50
                                  ? "var(--accent-healthy-fg)"
                                  : "var(--accent-warning-fg)"
                              }
                            >
                              {stage.overallConversionRate.toFixed(0)}%
                            </Text>
                          </Flex>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Text
                      color="var(--text-secondary-color)"
                      textAlign="center"
                      py={10}
                    >
                      {t("app.dashboard.noData")}
                    </Text>
                  )}
                </Card.Body>
              </Card.Root>
            </GridItem>
          </Grid>
        </>
      )}

      {/* ── USAGE ────────────────────────────────────────────────────────── */}
      {isUsage && (
        <Grid
          templateColumns={{ base: "1fr", lg: "repeat(12, 1fr)" }}
          gap={5}
          w="full"
        >
          <GridItem colSpan={{ base: 1, lg: 6 }}>
            <Card.Root h="full">
              <ChartCardHeader
                preLabel={`${t("app.dashboard.preHeaderUsers")} · ${rangeLabel}`}
                title={t("app.dashboard.charts.dailyActiveUsers")}
                refreshing={dailyUsersLoading && dailyUsersLoaded}
              />
              <Card.Body>
                {!dailyUsersLoaded ? (
                  <ChartSkeleton height={280} />
                ) : (
                  <LineChart
                    series={dailyActiveUsers}
                    height={280}
                    stacked={false}
                    enableArea={false}
                    enableSlices
                    integerYAxis
                    xAxisLabel={t("app.dashboard.charts.date")}
                    yAxisLabel={t("app.dashboard.charts.activeUsers")}
                  />
                )}
              </Card.Body>
            </Card.Root>
          </GridItem>

          <GridItem colSpan={{ base: 1, lg: 6 }}>
            <Card.Root h="full">
              <ChartCardHeader
                preLabel={`${t("app.dashboard.preHeaderActivity")} · ${rangeLabel}`}
                title={t("app.dashboard.charts.messages")}
                refreshing={messagesLoading && messagesLoaded}
              />
              <Card.Body>
                {!messagesLoaded ? (
                  <ChartSkeleton height={280} withLegend />
                ) : (
                  <BarChart
                    data={messagesData}
                    keys={
                      messagesData.length > 0
                        ? Object.keys(messagesData[0]).filter(
                            (k) => k !== "time",
                          )
                        : []
                    }
                    indexBy="time"
                    height={280}
                  />
                )}
              </Card.Body>
            </Card.Root>
          </GridItem>

          <GridItem colSpan={{ base: 1, lg: 12 }}>
            <Card.Root h="full">
              <ChartCardHeader
                preLabel={`${t("app.dashboard.preHeaderEndpoints")} · ${rangeLabel}`}
                title={t("app.dashboard.charts.topEndpoints")}
                refreshing={topEndpointsLoading && topEndpointsLoaded}
              />
              <Card.Body>
                {!topEndpointsLoaded ? (
                  <ChartSkeleton height={280} />
                ) : (
                  <BarChart
                    data={topEndpoints.map((d) => ({
                      toolName: d.toolName,
                      [t("app.dashboard.charts.success")]: d.successCount,
                      [t("app.dashboard.charts.error")]: d.errorCount,
                    }))}
                    keys={[
                      t("app.dashboard.charts.success"),
                      t("app.dashboard.charts.error"),
                    ]}
                    indexBy="toolName"
                    layout="horizontal"
                    height={280}
                    maxLabelLength={18}
                    truncateLabelFrom="start"
                  />
                )}
              </Card.Body>
            </Card.Root>
          </GridItem>

          <GridItem colSpan={{ base: 1, lg: 12 }}>
            <Card.Root h="full">
              <ChartCardHeader
                preLabel={`${t("app.dashboard.preHeaderSkills")} · ${rangeLabel}`}
                title={t("app.dashboard.charts.topSkills")}
                refreshing={topSkillsLoading && topSkillsLoaded}
              />
              <Card.Body>
                {!topSkillsLoaded ? (
                  <ChartSkeleton height={280} />
                ) : topSkills.length > 0 ? (
                  <BarChart
                    data={topSkills.map((d) => ({
                      skillName: d.skillName,
                      [t("app.dashboard.charts.uses")]: d.usageCount,
                    }))}
                    keys={[t("app.dashboard.charts.uses")]}
                    indexBy="skillName"
                    layout="horizontal"
                    height={280}
                    maxLabelLength={18}
                  />
                ) : (
                  <Text
                    color="var(--text-secondary-color)"
                    textAlign="center"
                    py={10}
                  >
                    {t("app.dashboard.noData")}
                  </Text>
                )}
              </Card.Body>
            </Card.Root>
          </GridItem>

          {/* Autonomous agent usage — full-width horizontal bar of runs per agent */}
          <GridItem colSpan={{ base: 1, lg: 12 }}>
            <Card.Root h="full">
              <ChartCardHeader
                preLabel={`${t("app.dashboard.preHeaderAutonomousUsage")} · ${rangeLabel}`}
                title={t("app.dashboard.charts.autonomousAgentUsage")}
                refreshing={autonomousUsageLoading && autonomousUsageLoaded}
              />
              <Card.Body>
                {!autonomousUsageLoaded ? (
                  <ChartSkeleton height={280} />
                ) : autonomousUsage.length > 0 ? (
                  <BarChart
                    data={autonomousUsage.map((d) => ({
                      agentName: d.agentName,
                      [t("app.dashboard.charts.runs")]: d.totalRuns,
                    }))}
                    keys={[t("app.dashboard.charts.runs")]}
                    indexBy="agentName"
                    layout="horizontal"
                    height={280}
                    maxLabelLength={18}
                  />
                ) : (
                  <Text
                    color="var(--text-secondary-color)"
                    textAlign="center"
                    py={10}
                  >
                    {t("app.dashboard.noData")}
                  </Text>
                )}
              </Card.Body>
            </Card.Root>
          </GridItem>

          {/* All-skills paged table + CSV export — visible to all users. */}
          <GridItem colSpan={{ base: 1, lg: 12 }}>
            <AllSkillsTableCard
              startDate={startDate}
              endDate={endDate}
              reloadKey={reloadKey}
              fromLabel={range.from}
              toLabel={range.to}
            />
          </GridItem>
        </Grid>
      )}

      {/* ── PERFORMANCE ──────────────────────────────────────────────────── */}
      {isPerformance && (
        <>
          <Grid
            templateColumns={{ base: "1fr", lg: "repeat(12, 1fr)" }}
            gap={5}
            w="full"
          >
            <GridItem colSpan={{ base: 1, lg: 6 }}>
              <Card.Root h="full">
                <ChartCardHeader
                  preLabel={`${t("app.dashboard.preHeaderTools")} · ${rangeLabel}`}
                  title={t("app.dashboard.charts.toolExecutions")}
                  refreshing={toolExecutionsLoading && toolExecutionsLoaded}
                />
                <Card.Body>
                  {!toolExecutionsLoaded ? (
                    <ChartSkeleton height={280} withLegend />
                  ) : (
                    <LineChart
                      series={toolExecutionsSeries}
                      height={280}
                      stacked={false}
                      enableArea={false}
                      enableSlices
                      xAxisLabel={t("app.dashboard.charts.time")}
                      yAxisLabel={t("app.dashboard.charts.executions")}
                    />
                  )}
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem colSpan={{ base: 1, lg: 6 }}>
              <Card.Root h="full">
                <Card.Header pb={2}>
                  <Box>
                    <PreHeader size="xs">
                      {t("app.dashboard.preHeaderSecurity")}
                    </PreHeader>
                    <HStack mt={2}>
                      <MdSecurity />
                      <Heading size="md">
                        {t("app.dashboard.charts.securityViolations")}
                      </Heading>
                    </HStack>
                  </Box>
                </Card.Header>
                <Card.Body>
                  {!securityLoaded ? (
                    <ChartSkeleton height={280} />
                  ) : securityViolations &&
                    securityViolations.violationsByTime.length > 0 ? (
                    <LineChart
                      series={[
                        {
                          id: "violations",
                          label: t("app.dashboard.charts.violations"),
                          data: securityViolations.violationsByTime.map(
                            (v) => ({
                              x: new Date(v.timestamp).toLocaleDateString(
                                "en-US",
                                { day: "numeric", month: "short" },
                              ),
                              y: v.violationCount,
                            }),
                          ),
                        },
                      ]}
                      height={280}
                      stacked={false}
                      enableArea={false}
                      enableSlices
                      xAxisLabel={t("app.dashboard.charts.date")}
                      yAxisLabel={t("app.dashboard.charts.violations")}
                    />
                  ) : (
                    <Text
                      color="var(--text-secondary-color)"
                      textAlign="center"
                      py={10}
                    >
                      {t("app.dashboard.noData")}
                    </Text>
                  )}
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem colSpan={{ base: 1, lg: 8 }}>
              <Card.Root h="full">
                <ChartCardHeader
                  preLabel={`${t("app.dashboard.preHeaderFeedback")} · ${rangeLabel}`}
                  title={t("app.dashboard.charts.feedbackTrend")}
                  refreshing={feedbackTrendLoading && feedbackTrendLoaded}
                />
                <Card.Body>
                  {!feedbackTrendLoaded ? (
                    <ChartSkeleton height={280} withLegend />
                  ) : (
                    <BarChart
                      data={feedbackTrend}
                      keys={["Positive", "Negative"]}
                      indexBy="date"
                      height={280}
                    />
                  )}
                </Card.Body>
              </Card.Root>
            </GridItem>

            <GridItem colSpan={{ base: 1, lg: 4 }}>
              <Card.Root h="full">
                <ChartCardHeader
                  preLabel={`${t("app.dashboard.preHeaderFeedback")} · ${rangeLabel}`}
                  title={t("app.dashboard.charts.negativeFeedbackByReason")}
                  refreshing={feedbackReasonsLoading && feedbackReasonsLoaded}
                />
                <Card.Body>
                  {!feedbackReasonsLoaded ? (
                    <ChartSkeleton height={280} />
                  ) : (
                    <BarChart
                      data={negativeFeedbackReasons}
                      keys={["count"]}
                      indexBy="reason"
                      height={280}
                    />
                  )}
                </Card.Body>
              </Card.Root>
            </GridItem>
          </Grid>

          {/* Session Quality KPIs */}
          <Box>
            <Box mb={4}>
              <PreHeader size="md">
                {t("app.dashboard.sections.insights")}
              </PreHeader>
            </Box>
            {!qualityLoaded ? (
              <ChartSkeleton height={120} />
            ) : sessionQuality ? (
              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(4, 1fr)",
                }}
                gap={5}
              >
                <KpiTile
                  tone="flat"
                  label={t("app.dashboard.quality.avgDuration")}
                  value={`${sessionQuality.quality.averageSessionDurationMinutes.toFixed(1)}`}
                  sublabel={t("app.common.minutes")}
                />
                <KpiTile
                  tone="flat"
                  label={t("app.dashboard.quality.avgMessages")}
                  value={sessionQuality.quality.averageMessagesPerSession.toFixed(
                    1,
                  )}
                  sublabel={t("app.dashboard.kpis.perSession")}
                />
                <KpiTile
                  tone="flat"
                  label={t("app.dashboard.quality.bounceRate")}
                  value={`${sessionQuality.quality.bounceRate.toFixed(1)}%`}
                  trend={
                    sessionQuality.quality.bounceRate > 50 ? "down" : "flat"
                  }
                />
                <KpiTile
                  tone="flat"
                  label={t("app.dashboard.quality.resolutionRate")}
                  value={`${sessionQuality.quality.resolutionRate.toFixed(1)}%`}
                  trend={
                    sessionQuality.quality.resolutionRate > 50 ? "up" : "down"
                  }
                />
              </Grid>
            ) : (
              <Text
                color="var(--text-secondary-color)"
                textAlign="center"
                py={10}
              >
                {t("app.dashboard.noData")}
              </Text>
            )}
          </Box>

          {/* Customer Health table */}
          <Card.Root id="customer-health-section">
            <Card.Header pb={2}>
              <HStack justify="space-between" wrap="wrap" gap={3}>
                <Box>
                  <PreHeader size="xs">
                    {t("app.dashboard.preHeaderCustomers")}
                  </PreHeader>
                  <HStack mt={2}>
                    <Tooltip
                      content={
                        <VStack align="start" gap={1}>
                          <Text fontWeight="bold">
                            {t(
                              "app.dashboard.health.healthScoreAlgorithmTitle",
                            )}
                          </Text>
                          <Text>
                            {t("app.dashboard.health.healthScoreFormula")}
                          </Text>
                          <Text fontSize="xs" mt={2}>
                            <strong>
                              {t("app.dashboard.health.statusLabel")}
                            </strong>{" "}
                            {t("app.dashboard.health.statusRanges")}
                          </Text>
                        </VStack>
                      }
                      showArrow
                    >
                      <MdPeople style={{ cursor: "help" }} />
                    </Tooltip>
                    <Heading size="md">
                      {t("app.dashboard.charts.customerHealth")}
                    </Heading>
                  </HStack>
                </Box>
                {healthLoaded && customerHealth && (
                  <HStack gap={4}>
                    <HStack gap={1.5}>
                      <MdCheckCircle color="var(--accent-healthy-rule)" />
                      <Text fontSize="sm">
                        {customerHealth.healthyCount}{" "}
                        {t("app.dashboard.health.healthy")}
                      </Text>
                    </HStack>
                    <HStack gap={1.5}>
                      <MdWarning color="var(--accent-warning-rule)" />
                      <Text fontSize="sm">
                        {customerHealth.atRiskCount}{" "}
                        {t("app.dashboard.health.atRisk")}
                      </Text>
                    </HStack>
                    <HStack gap={1.5}>
                      <MdError color="var(--accent-risk-rule)" />
                      <Text fontSize="sm">
                        {customerHealth.churningCount}{" "}
                        {t("app.dashboard.health.churning")}
                      </Text>
                    </HStack>
                  </HStack>
                )}
              </HStack>
            </Card.Header>
            <Card.Body>
              {!healthLoaded ? (
                <ChartSkeleton height={300} />
              ) : customerHealth && customerHealth.customers.length > 0 ? (
                <Box overflowX="auto">
                  <Table.Root size="sm" minW="600px">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>
                          {t("app.dashboard.health.customer")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader>
                          {t("app.dashboard.health.score")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader>
                          {t("app.dashboard.health.status")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader>
                          {t("app.dashboard.health.lastActivity")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader>
                          {t("app.dashboard.health.sessions")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader>
                          {t("app.dashboard.health.successRate")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader>
                          {t("app.dashboard.health.riskFactors")}
                        </Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {customerHealth.customers
                        .slice(0, 10)
                        .map((customer, idx) => (
                          <Table.Row key={idx}>
                            <Table.Cell
                              title={
                                customer.customerName || customer.customerEmail
                                  ? customer.customerExternalId
                                  : undefined
                              }
                            >
                              {customer.customerName ||
                                customer.customerEmail ||
                                customer.customerExternalId}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                colorPalette={
                                  customer.status ===
                                  CustomerHealthStatus.Healthy
                                    ? "green"
                                    : customer.status ===
                                        CustomerHealthStatus.AtRisk
                                      ? "orange"
                                      : "red"
                                }
                              >
                                {customer.healthScore}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              {customer.status ===
                                CustomerHealthStatus.Healthy && (
                                <Badge colorPalette="green">
                                  {t("app.dashboard.health.healthy")}
                                </Badge>
                              )}
                              {customer.status ===
                                CustomerHealthStatus.AtRisk && (
                                <Badge colorPalette="orange">
                                  {t("app.dashboard.health.atRisk")}
                                </Badge>
                              )}
                              {customer.status ===
                                CustomerHealthStatus.Churning && (
                                <Badge colorPalette="red">
                                  {t("app.dashboard.health.churning")}
                                </Badge>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {customer.daysSinceLastActivity}{" "}
                              {t("app.common.daysAgo")}
                            </Table.Cell>
                            <Table.Cell>{customer.totalSessions}</Table.Cell>
                            <Table.Cell>
                              {customer.successRate.toFixed(1)}%
                            </Table.Cell>
                            <Table.Cell>
                              <VStack align="start" gap={1}>
                                {customer.riskFactors.map((factor, fIdx) => (
                                  <Text
                                    key={fIdx}
                                    fontSize="xs"
                                    color="var(--accent-risk-fg)"
                                  >
                                    {factor}
                                  </Text>
                                ))}
                              </VStack>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                    </Table.Body>
                  </Table.Root>
                </Box>
              ) : (
                <Text
                  color="var(--text-secondary-color)"
                  textAlign="center"
                  py={10}
                >
                  {t("app.dashboard.noData")}
                </Text>
              )}
            </Card.Body>
          </Card.Root>
        </>
      )}
    </Stack>
  );
};

export default Dashboard;
